import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import Analytics from 'analytics-opencare';

import { SPECIALTY_FLOW } from 'pages/funnel/specialtyFlow/config';
import { useAnalyticsService } from 'services/AnalyticsService';

import SingleSelect from '../../../common/components/SingleSelect/SingleSelect';
import { OptionBlock, Title } from '../../../common/styled-components';
import { useFunnelStorage } from '../../storage/Storage';
import { StepWrapper } from '../../styled-components';
import { generateStepAnalytics, STEP_KEY } from '../../utils';

import { isUrgentOptions } from './constants';
import { SpecialistTriaging } from './SpecialistTriaging';

const analytics = new Analytics();

const Reason = ({ position, moveToNextStep }) => {
  const storage = useFunnelStorage();
  const history = useHistory();
  const analyticsService = useAnalyticsService();

  const savedOption = storage.get(STEP_KEY.REASON);

  useEffect(() => {
    const userUsedBrowserButtons = history.action === 'POP';

    if (!savedOption || userUsedBrowserButtons) {
      analytics.trackPageView({
        pageType: analytics.pageTypes.FUNNEL,
        pageName: 'funnel_reason',
        ...generateStepAnalytics(position, storage),
        ...analyticsService.analyticsData,
      });
    }
  }, []);

  const selectAnswer = (selectedOptionId, index) => {
    const choice = isUrgentOptions.find(
      (option) => option.id === selectedOptionId,
    );

    analytics.trackClick({
      pageType: analytics.pageTypes.FUNNEL,
      pageName: 'funnel_reason',
      name: 'wizard',
      section: 'isUrgent',
      position: [index + 1],
      ...generateStepAnalytics(position, storage, {
        response: choice.title,
      }),
    });

    storage.save(STEP_KEY.REASON, { isUrgent: choice.id }, true);

    if (choice.id === 'Yes') {
      // The funnel reset is needed to update the steps order.
      moveToNextStep({ reset: storage.getActiveConfig()?.subId === 1017 });
    } else if (choice.id === 'No') {
      const isEmptyAfterReasonStep = storage.isEmpty({
        excludedSteps: [STEP_KEY.WELCOME, STEP_KEY.LOCATION, STEP_KEY.REASON],
      });

      // The funnel reset is needed to update the steps order.
      moveToNextStep({
        reset:
          storage.getActiveConfig()?.subId !== 1017 && isEmptyAfterReasonStep,
      });
    }
  };

  const funnelConfigId = storage.getActiveConfig()?.id;

  if (
    [1020, SPECIALTY_FLOW.CONFIG_ID].includes(funnelConfigId)
    || savedOption?.wasVariant
  ) {
    return <SpecialistTriaging moveToNextStep={moveToNextStep} />;
  }

  return (
    <div data-testid="main-reason">
      <Title id="title">Do you have a dental emergency?</Title>
      <StepWrapper>
        <form>
          <OptionBlock row={3}>
            {isUrgentOptions.map((option, i) => (
              <SingleSelect
                key={option.id}
                name="isUrgent"
                id={option.id}
                title={option.title}
                description={option.description}
                describedBy="title"
                onChange={(e) => selectAnswer(e, i)}
                selected={savedOption?.isUrgent === option.title}
              />
            ))}
          </OptionBlock>
        </form>
      </StepWrapper>
    </div>
  );
};

Reason.defaultProps = {
  position: '',
};

Reason.propTypes = {
  position: PropTypes.string,
  moveToNextStep: PropTypes.func.isRequired,
};

export default Reason;
